import {
  AggregationModule,
  CellStyleModule,
  CheckboxEditorModule,
  ClientSideRowModelApiModule,
  ClientSideRowModelModule,
  ClipboardModule,
  ColDef,
  ColumnApiModule,
  ColumnAutoSizeModule,
  createGrid,
  DateEditorModule,
  GridApi,
  GridOptions,
  GridStateModule,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  LargeTextEditorModule,
  LicenseManager,
  MasterDetailModule,
  ModuleRegistry,
  NumberEditorModule,
  PaginationModule,
  PinnedRowModule,
  RenderApiModule,
  RichSelectModule,
  RowApiModule,
  RowAutoHeightModule,
  RowGroupingModule,
  RowSelectionModule,
  ServerSideRowModelModule,
  ServerSideRowModelApiModule,
  TextEditorModule,
  TextFilterModule,
  TooltipModule,
  TreeDataModule,
  QuickFilterModule,
} from "ag-grid-enterprise";

import type {
  AsyncTransactionsFlushedEvent,
  CellClassParams,
  CellContextMenuEvent,
  CellEditingStartedEvent,
  CellEditingStoppedEvent,
  CellValueChangedEvent,
  ColumnMovedEvent,
  ColumnPinnedEvent,
  ColumnResizedEvent,
  ColumnVisibleEvent,
  FirstDataRenderedEvent,
  GridReadyEvent,
  GridState,
  ITooltipParams,
  ModelUpdatedEvent,
  SelectionChangedEvent,
  SortChangedEvent,
  SuppressKeyboardEventParams,
} from "ag-grid-enterprise";

import { CustomElementCellEditor } from "./data_grid/custom_element_editor";
import { InlineLargeTextCellEditor } from "./data_grid/inline_large_text_cell_editor";
import { CustomElementCellRenderer } from "./data_grid/custom_element_renderer";
import { DetailCellRenderer } from "./data_grid/detail_cell_renderer";
import { withDefaultHeaderTooltips } from "./data_grid/header_tooltips";
import { HtmlRenderer } from "./data_grid/html_renderer";
import { LoadingCellRenderer } from "./data_grid/loading_cell_renderer";
import { parseUrl, buildUrl } from "./data_grid/url_utils";
import {
  ContextMenuConfig,
  handleRowContextMenu,
} from "./data_grid/context_menu";

import { get } from "@rails/request.js";

type QueryParams = Record<string, string | string[]>;

function formDataToQueryParams(form: HTMLFormElement): QueryParams {
  const formData = new FormData(form);
  const result: QueryParams = {};
  for (const key of formData.keys()) {
    if (key in result) continue;
    const values = formData.getAll(key) as string[];
    result[key] = key.endsWith("[]") ? values : values[0];
  }
  return result;
}

async function readJsonBody(response: { json: unknown }): Promise<any> {
  const body = response.json;

  return typeof body === "function"
    ? (body as () => Promise<unknown>).call(response)
    : body;
}

ModuleRegistry.registerModules([
  AggregationModule,
  CellStyleModule,
  CheckboxEditorModule,
  ClientSideRowModelApiModule,
  ClientSideRowModelModule,
  ClipboardModule,
  ColumnApiModule,
  ColumnAutoSizeModule,
  DateEditorModule,
  GridStateModule,
  LargeTextEditorModule,
  MasterDetailModule,
  NumberEditorModule,
  PaginationModule,
  PinnedRowModule,
  QuickFilterModule,
  RenderApiModule,
  RichSelectModule,
  RowApiModule,
  RowAutoHeightModule,
  RowGroupingModule,
  RowSelectionModule,
  ServerSideRowModelApiModule,
  ServerSideRowModelModule,
  TextEditorModule,
  TextFilterModule,
  TooltipModule,
  TreeDataModule,
]);

LicenseManager.setLicenseKey(
  "Using_this_{AG_Grid}_Enterprise_key_{AG-094272}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Themis_Solutions_Inc._dba_Clio}_is_granted_a_{Multiple_Applications}_Developer_License_for_{40}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Grid}_Enterprise___This_key_has_been_granted_a_Deployment_License_Add-on_for_{3}_Production_Environments___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{26_November_2026}____[v3]_[01]_MTc5NTY1MTIwMDAwMA==e035bfcc810ff7b55ff8db81636e75fe"
);

type PersistedState = {
  version: number;
  gridState: GridState;
  isTextWrapped?: boolean;
  meta?: {
    savedAt?: string;
  };
};

// AG Grid's ColDef extended with Clio-specific keys that processColumnDefs
// recognises and translates into AG Grid's native cellRenderer / cellEditor
// component contract before the grid sees them.
type ClioColDef = ColDef & {
  cellRendererElement?: string;
  cellRendererElementAttributes?: Record<string, string>;
  cellEditorElement?: string;
  // Attributes forwarded to the editor's element. For a custom-element editor
  // (`cellEditorElement`) they're set at construction; for a built-in editor
  // (e.g. LargeTextCellEditor) they're stamped onto its input element on
  // cellEditingStarted, after AG Grid has created it.
  cellEditorElementAttributes?: Record<string, string>;
};

interface AgGridConfig {
  dataStrategy: "clientSide" | "serverSide";
  dataUrl?: string;
  columnDefs?: ClioColDef[];
  rowData?: any[];
  gridOptions?: GridOptions;
  hasCustomDetailTemplate?: boolean;
  noRecordsTemplate?: string;
  noMatchesTemplate?: string;
  overlayLoadingIndicatorTemplate?: string;
  cellLoadingIndicatorTemplate?: string;
  enableUrlStatePersistence?: boolean;
  page?: number;

  // Explicit client-side persistence identifier. When provided, the grid will
  // persist AG Grid ColumnState to localStorage under this key.
  persistenceId?: string;

  // AG Grid GridState keys to omit from localStorage persistence. Exclusions
  // apply when reading existing snapshots and writing future snapshots.
  excludedPersistedStateKeys?: readonly (keyof GridState)[];

  // Initial "Expand rows" (text wrap) state for a user with no saved
  // preference. A stored preference always wins. Sent only on opt-in.
  defaultExpandRows?: boolean;

  // Row IDs to preselect when the grid loads. These rows will be automatically
  // selected after the grid data is loaded. IDs must be strings.
  preselectedRowIds?: string[];

  // When present, wires right-click anywhere in a row to open the popover
  // identified by `triggerSelector` at the cursor.
  contextMenu?: ContextMenuConfig;
}

const DEFAULT_PAGE_SIZE = 50;

const filterPersistedGridState = (
  gridState: GridState,
  excludedKeys: readonly (keyof GridState)[] | undefined
): GridState => {
  if (!Array.isArray(excludedKeys) || excludedKeys.length === 0) {
    return gridState;
  }

  const filteredState = { ...gridState };
  for (const key of excludedKeys) {
    delete filteredState[key];
  }

  return filteredState;
};

/**
 * Buffer pages added to the estimated row count during page redirection.
 * This ensures AG Grid's pagination controls remain functional while we skip
 * unwanted page fetches. See handlePageRedirection() for details.
 */
const PAGINATION_BUFFER_PAGES = 2;

export default (config: AgGridConfig) => ({
  config,
  gridApi: null as GridApi | null,
  _persistTimer: null as ReturnType<typeof setTimeout> | null,
  _urlSyncTimer: null as ReturnType<typeof setTimeout> | null,
  _previousFormKeys: null as Set<string> | null,
  currentPage: 0,
  totalPages: 0,
  totalRowCount: 0,
  isRowCountCapped: false,
  currentPageSize: config.gridOptions?.paginationPageSize || DEFAULT_PAGE_SIZE,
  selectedRows: [] as any[],
  cellErrors: {} as Record<string, Record<string, string[]>>,

  /**
   * Used to indicate that the grid is filtered, and the no matches overlay will
   * be shown. Only used in server-side row model.
   */
  hasExternalFilter: false,

  /**
   * The target startRow for page redirection. Used to optimize page navigation
   * by skipping unnecessary data fetches.
   *
   * AG Grid's Server-Side Row Model (SSRM) always starts by requesting page 0
   * when the datasource is set or when `refreshServerSide()` is called. This
   * causes problems when refreshing from a page other than the first, as AG
   * Grid resets to page 0, which resets the user's position in the pagination
   * sequence.
   */
  _redirectTargetStartRow: null as number | null,

  initDataGrid() {
    const container = this.$el;
    this.installLinkClickEventGuards(container);
    const gridOptions = this.buildGridOptions();
    this.gridApi = createGrid(container, gridOptions);

    // Restore the user's saved text wrap choice, or apply the grid's default
    if (this.resolveTextWrapState()) {
      this.setTextWrapState(true);
    }

    // Sync Alpine state with the grid's actual pagination state.
    // This ensures currentPageSize reflects the persisted value from localStorage.
    this.updatePaginationInfo();

    // Seed the previous-form-keys tracker from the intersection of the
    // browser URL params and the dataUrl params. The intersection isolates
    // form-managed filter keys — the URL may contain unrelated params (UTM,
    // route params) and the dataUrl may contain server-only params (preview,
    // format). Keys in both are the bookmarked filter values the server
    // baked into the grid, so submitQueryParams can detect when the user
    // clears them before ever clicking Apply.
    if (this.config.dataUrl && this.config.enableUrlStatePersistence) {
      const { queryParams: urlParams } = parseUrl();
      const [, dataUrlParamString] = this.config.dataUrl.split("?");
      if (dataUrlParamString) {
        const dataUrlKeys = new Set(
          new URLSearchParams(dataUrlParamString).keys()
        );
        const seed = new Set<string>();
        for (const key of urlParams.keys()) {
          if (dataUrlKeys.has(key)) {
            seed.add(key);
          }
        }
        if (seed.size > 0) {
          this._previousFormKeys = seed;
        }
      }
    }
  },

  installLinkClickEventGuards(container: HTMLElement) {
    container.addEventListener("click", this.handleGridLinkClick.bind(this), {
      capture: true,
    });
    container.addEventListener(
      "auxclick",
      this.handleGridLinkClick.bind(this),
      {
        capture: true,
      }
    );
  },

  handleGridLinkClick(event: MouseEvent) {
    if (this.isModifiedLinkClick(event)) {
      event.stopPropagation();
    }
  },

  isModifiedLinkClick(event: MouseEvent): boolean {
    const target = event.target;
    if (!(target instanceof Element)) return false;

    const link = target.closest("a[href]");
    if (!link) return false;

    return (
      event.ctrlKey || event.metaKey || event.shiftKey || event.button === 1
    );
  },

  buildGridOptions(): GridOptions {
    const defaults: GridOptions = {
      domLayout: "normal",
      pagination: true,
      paginationPageSize: this.currentPageSize,
      rowBuffer: this.currentPageSize,
      suppressPaginationPanel: true,
      suppressDragLeaveHidesColumns: true,
      suppressMovableColumns: true,
      suppressColumnMoveAnimation: true,
      suppressMultiSort: true,
      headerHeight: 38,
      tooltipShowDelay: 0,
      masterDetail: this.config.hasCustomDetailTemplate,
      detailCellRenderer: DetailCellRenderer,
      detailRowAutoHeight: true,
      loading: true,
      singleClickEdit: true,
      stopEditingWhenCellsLoseFocus: true,
      overlayLoadingTemplate: this.config.overlayLoadingIndicatorTemplate,
      loadingCellRenderer: LoadingCellRenderer,
      loadingCellRendererParams: {
        loadingIndicatorTemplate: this.config.cellLoadingIndicatorTemplate,
      },
      defaultColDef: {
        autoHeight: true,
        sortable: false,
        filter: false,
        resizable: false,
        unSortIcon: true,
        minWidth: 120,
        suppressKeyboardEvent: this.suppressKeyboardEvent.bind(this),
        cellStyle: {
          lineHeight: "var(--clio-ui-data-grid-cell-line-height)",
          paddingTop: "var(--clio-ui-data-grid-cell-padding-vertical)",
          paddingBottom: "var(--clio-ui-data-grid-cell-padding-vertical)",
        },
        cellClass: (params: CellClassParams) => {
          const classes: string[] = [];
          const field = params.colDef?.field;
          const rowId = params.node?.id;

          // Server-evaluated cellClass lambda result (stored in row data)
          if (field) {
            const serverClass = params.data?.[`${field}_cellClass`];
            if (serverClass) {
              classes.push(
                ...(Array.isArray(serverClass) ? serverClass : [serverClass])
              );
            }
          }

          // Error classes
          const errors = this.getCellErrors(rowId, field);
          if (errors?.length) {
            classes.push("ag-cell-data-error");
          }

          // Tag cells AG Grid considers editable so the stylesheet can reveal a
          // hover pencil hint (see data_grid.plugin.cjs). Use isCellEditable
          // rather than the raw `${field}_editable` flag so per-column `editable`
          // overrides are honoured too.
          if (params.node && params.column?.isCellEditable(params.node)) {
            classes.push("clio-ui-data-grid-cell-editable");
          }

          return classes.length ? classes : null;
        },
        tooltipValueGetter: this.defaultTooltipValueGetter.bind(this),
        // Reads the per-row editability flag stashed at `${field}_editable`
        // by DataGridService.process_editable. Per-column boolean `editable`
        // overrides this default — AG Grid's per-column-wins precedence.
        editable: (params) => {
          const field = (params.colDef as ColDef | undefined)?.field;
          if (!field) return false;
          return Boolean(params.data?.[`${field}_editable`]);
        },
      },
      components: this.buildCustomComponents(),
      onPaginationChanged: () => {
        this.updatePaginationInfo();
        this.syncGridStateToUrlParams();

        // Save pagination state to localStorage for persistence across sessions.
        // This ensures page size preferences are remembered.
        if (this.gridApi) {
          this.savePersistedStateDebounced(this.gridApi);
        }
      },
    };

    const strategyOptions = this.getStrategyOptions();
    const consumerOptions = (this.config.gridOptions ||
      {}) as Partial<GridOptions>;

    // Tree data callbacks are declared as Ruby lambdas on the facade's
    // `options` method and evaluated server-side per row by `DataGridService`,
    // mirroring how `cellClass` and `cellRenderer` lambdas are handled. The
    // results are stored on each row as `_isGroup` / `_groupKey`; these shims
    // wire AG Grid's callbacks to read those fields when `treeData: true`.
    const treeDataDefaults: Partial<GridOptions> = consumerOptions.treeData
      ? {
          isServerSideGroup: (data: { _isGroup?: boolean }) =>
            Boolean(data?._isGroup),
          getServerSideGroupKey: (data: {
            id?: string | number;
            _groupKey?: string | number;
          }) => String(data?._groupKey ?? data?.id ?? ""),
        }
      : {};

    const persistedState = this.loadPersistedState();

    // Apply flex: 1 to columns if there's no persisted state
    const flexConfig = !persistedState ? { flex: 1 } : {};

    // Deep merge defaultColDef to allow partial overrides
    const mergedDefaultColDef = {
      ...defaults.defaultColDef,
      ...flexConfig,
      ...(strategyOptions.defaultColDef || {}),
      ...(consumerOptions.defaultColDef || {}),
    };

    // Hook into AG Grid events to trigger our own internal workflows
    const dataGridHandlers = {
      onSortChanged: (event: SortChangedEvent) => {
        this.clearWindowedSelection();
        this.savePersistedStateDebounced(event.api);
        this.syncGridStateToUrlParams();

        const sortModel = event.api
          .getColumnState()
          .filter((col: any) => col.sort)
          .map((col: any) => ({ colId: col.colId, sort: col.sort }));
        this.$dispatch("data-grid:sort", { sortModel });
      },
      onColumnMoved: (event: ColumnMovedEvent) =>
        this.savePersistedStateDebounced(event.api),
      onColumnPinned: (event: ColumnPinnedEvent) =>
        this.savePersistedStateDebounced(event.api),
      onColumnVisible: (event: ColumnVisibleEvent) =>
        this.savePersistedStateDebounced(event.api),
      onColumnResized: (event: ColumnResizedEvent) => {
        if (event.finished) this.savePersistedStateDebounced(event.api);
      },
      onModelUpdated: (event: ModelUpdatedEvent) => {
        this.handleNoRowsOverlay(event);
      },
      onSelectionChanged: (event: SelectionChangedEvent) => {
        this.handleSelectionChanged(event);
      },
      onAsyncTransactionsFlushed: (_event: AsyncTransactionsFlushedEvent) => {
        this.refreshSelectedRowsFromGrid();
      },
      onFirstDataRendered: (event: FirstDataRenderedEvent) => {
        this.applyPreselectedRows();
      },
      onCellValueChanged: (event: CellValueChangedEvent) => {
        this.handleCellValueChanged(event);
      },
      onCellEditingStarted: (event: CellEditingStartedEvent) => {
        this.applyCellEditorElementAttributes(event);
      },
      onCellEditingStopped: (event: CellEditingStoppedEvent) => {
        this.restoreRowHeightAfterInlineLargeText(event);
      },
    };

    // If persisted state has a page size, use it to override the config.
    // This ensures paginationPageSize and initialState.pagination.pageSize are aligned,
    // preventing AG Grid from reporting the wrong page size via paginationGetPageSize().
    // Also override rowBuffer and cacheBlockSize (for serverSide) to keep them synchronized,
    // matching the behavior in changePageSize().
    const persistedPageSize = persistedState?.gridState?.pagination?.pageSize;
    const paginationOverrides = persistedPageSize
      ? {
          paginationPageSize: persistedPageSize,
          rowBuffer: persistedPageSize,
          ...(this.config.dataStrategy === "serverSide"
            ? { cacheBlockSize: persistedPageSize }
            : {}),
        }
      : {};

    const contextMenu = this.config.contextMenu;
    const contextMenuOverrides: Partial<GridOptions> =
      contextMenu?.triggerSelector
        ? {
            preventDefaultOnContextMenu: true,
            onCellContextMenu: (event: CellContextMenuEvent) => {
              handleRowContextMenu(
                event.event as MouseEvent | null,
                contextMenu.triggerSelector
              );
              consumerOptions.onCellContextMenu?.(event);
            },
          }
        : {};

    // Apply partialColumnState for any exclusion. It is a no-op for non-column
    // keys and protects consumers that exclude column state from unintended resets.
    const hasPersistedStateExclusions =
      Array.isArray(this.config.excludedPersistedStateKeys) &&
      this.config.excludedPersistedStateKeys.length > 0;

    const initialState = persistedState?.gridState
      ? {
          ...persistedState.gridState,
          ...(hasPersistedStateExclusions ? { partialColumnState: true } : {}),
        }
      : undefined;

    return {
      ...defaults,
      ...strategyOptions,
      ...treeDataDefaults,
      ...consumerOptions,
      ...paginationOverrides,
      ...contextMenuOverrides,
      initialState,
      onGridReady: (params: GridReadyEvent) => {
        strategyOptions.onGridReady?.(params);
        consumerOptions.onGridReady?.(params);
      },
      onFirstDataRendered: (event: FirstDataRenderedEvent) => {
        dataGridHandlers.onFirstDataRendered(event);
        consumerOptions.onFirstDataRendered?.(event);
      },
      onSortChanged: (event: SortChangedEvent) => {
        dataGridHandlers.onSortChanged(event);
        consumerOptions.onSortChanged?.(event);
      },
      onColumnMoved: (event: ColumnMovedEvent) => {
        dataGridHandlers.onColumnMoved(event);
        consumerOptions.onColumnMoved?.(event);
      },
      onColumnPinned: (event: ColumnPinnedEvent) => {
        dataGridHandlers.onColumnPinned(event);
        consumerOptions.onColumnPinned?.(event);
      },
      onColumnVisible: (event: ColumnVisibleEvent) => {
        dataGridHandlers.onColumnVisible(event);
        consumerOptions.onColumnVisible?.(event);
      },
      onColumnResized: (event: ColumnResizedEvent) => {
        dataGridHandlers.onColumnResized(event);
        consumerOptions.onColumnResized?.(event);
      },
      onModelUpdated: (event: ModelUpdatedEvent) => {
        dataGridHandlers.onModelUpdated(event);
        consumerOptions.onModelUpdated?.(event);
      },
      onSelectionChanged: (event: SelectionChangedEvent) => {
        dataGridHandlers.onSelectionChanged(event);
        consumerOptions.onSelectionChanged?.(event);
      },
      onAsyncTransactionsFlushed: (event: AsyncTransactionsFlushedEvent) => {
        dataGridHandlers.onAsyncTransactionsFlushed(event);
        consumerOptions.onAsyncTransactionsFlushed?.(event);
      },
      onCellValueChanged: (event: CellValueChangedEvent) => {
        dataGridHandlers.onCellValueChanged(event);
        consumerOptions.onCellValueChanged?.(event);
      },
      onCellEditingStarted: (event: CellEditingStartedEvent) => {
        dataGridHandlers.onCellEditingStarted(event);
        consumerOptions.onCellEditingStarted?.(event);
      },
      onCellEditingStopped: (event: CellEditingStoppedEvent) => {
        dataGridHandlers.onCellEditingStopped(event);
        consumerOptions.onCellEditingStopped?.(event);
      },
      defaultColDef: mergedDefaultColDef,
      ...(this.config.columnDefs && {
        columnDefs: this.processColumnDefs(mergedDefaultColDef),
      }),
    };
  },

  /**
   * Process column definitions for handling custom HTML within individual cells
   * and within the custom detail, if applicable. Custom cell HTML is routed
   * through our custom htmlRenderer service and an additional display field is
   * created to store the rendered HTML content. Meanwhile, the custom detail
   * template is also processed to add an customDetailContent field to the row
   * data.
   */
  processColumnDefs(defaultColDef?: ColDef): ClioColDef[] {
    if (!this.config.columnDefs) return [];

    const processedColumns = this.config.columnDefs.map(
      (colDef: ClioColDef) => {
        // Start with a copy of the column definition
        let processedColumn: ClioColDef = { ...colDef };

        // Normalise cellEditor: callers will omit the "ag" prefix (e.g.
        // "TextCellEditor") and we add it here so the facade layer stays
        // decoupled from AG Grid's internal naming convention.
        if (
          processedColumn.cellEditor &&
          typeof processedColumn.cellEditor === "string"
        ) {
          processedColumn.cellEditor = `ag${processedColumn.cellEditor}`;
        }

        if (colDef.cellEditor === "LargeTextCellEditor") {
          if (colDef.cellEditorPopup) {
            console.warn(
              `[DATA GRID] cellEditorPopup is ignored on column "${colDef.field}": ` +
                `"LargeTextCellEditor" always edits in the cell. Remove the flag.`
            );
          }
          processedColumn.cellEditor = InlineLargeTextCellEditor;
          delete processedColumn.cellEditorPopup;
          processedColumn.context = {
            ...processedColumn.context,
            inlineLargeText: true,
          };
        }

        if (typeof colDef.cellEditorElement === "string") {
          processedColumn.cellEditor = CustomElementCellEditor;
          processedColumn.cellEditorParams = {
            ...processedColumn.cellEditorParams,
            tagName: colDef.cellEditorElement,
            ...(colDef.cellEditorElementAttributes
              ? { attributes: colDef.cellEditorElementAttributes }
              : {}),
          };
        } else if (colDef.cellEditorElementAttributes) {
          // Built-in editor: AG Grid owns its construction, so the attributes
          // are stamped onto the editor's input element on cellEditingStarted.
          // Stash them on `context` (AG Grid's arbitrary-data slot) for the
          // handler, and drop the top-level key so the data lives in one place.
          processedColumn.context = {
            ...processedColumn.context,
            cellEditorElementAttributes: colDef.cellEditorElementAttributes,
          };
          delete processedColumn.cellEditorElementAttributes;
        }

        // RichSelectCellEditor expects `cellEditorParams.values` as an array of
        // `{ label, value }` hashes. Inject the AG Grid callbacks needed to
        // render labels in the editor pill, dropdown rows, and read-only cells
        // while persisting only the value to the underlying row data.
        if (processedColumn.cellEditor === "agRichSelectCellEditor") {
          type RichSelectOption = {
            label: string;
            value: string | number | boolean;
          };

          const richValues: RichSelectOption[] =
            processedColumn.cellEditorParams?.values ?? [];

          const labelMap = new Map<string, string>(
            richValues.map(({ value, label }) => [String(value), label])
          );

          const formatValue = (v: unknown) =>
            v == null ? "" : (labelMap.get(String(v)) ?? String(v));

          processedColumn = {
            ...processedColumn,
            valueFormatter: (p) => formatValue(p.value),
            cellEditorParams: {
              ...processedColumn.cellEditorParams,
              values: richValues.map(({ value }) => value),
              formatValue,
            },
          };
        }

        // Handle custom cells - convert any cellRenderer to htmlRenderer configuration
        if (colDef.cellRenderer) {
          const displayField = `${colDef.field}_display`;

          processedColumn.cellRenderer = "htmlRenderer";
          processedColumn.cellRendererParams = {
            displayField: displayField,
            ...colDef.cellRendererParams, // Preserve any existing params
          };
        }

        if (typeof colDef.cellRendererElement === "string") {
          processedColumn.cellRenderer = CustomElementCellRenderer;
          processedColumn.cellRendererParams = {
            ...processedColumn.cellRendererParams,
            tagName: colDef.cellRendererElement,
            ...(colDef.cellRendererElementAttributes
              ? { attributes: colDef.cellRendererElementAttributes }
              : {}),
          };
        }

        // A showRowGroup column must use agGroupCellRenderer so AG Grid
        // renders the expand/collapse chevron (required for
        // treeDataDisplayType: "custom"). Nest the column's own renderer
        // as innerRenderer so its cell content still renders in the group
        // cell.
        if (colDef.showRowGroup) {
          processedColumn.cellRendererParams = {
            ...processedColumn.cellRendererParams,
            ...(processedColumn.cellRenderer
              ? { innerRenderer: processedColumn.cellRenderer }
              : {}),
          };
          processedColumn.cellRenderer = "agGroupCellRenderer";
        }

        return processedColumn;
      }
    );

    // A showRowGroup first column is already a group cell and draws the
    // detail chevron itself. Wrapping it again would nest agGroupCellRenderer
    // inside itself, which AG Grid ignores, dropping the cell content.
    if (
      this.config.hasCustomDetailTemplate &&
      processedColumns.length > 0 &&
      !processedColumns[0].showRowGroup
    ) {
      const firstColumn = processedColumns[0];
      const originalParams = firstColumn.cellRendererParams || {};

      processedColumns[0] = {
        ...firstColumn,
        cellRenderer: "agGroupCellRenderer",
        cellRendererParams: {
          innerRenderer: firstColumn.cellRenderer || undefined,
          innerRendererParams: originalParams,
        },
      };
    }

    return withDefaultHeaderTooltips(
      processedColumns,
      defaultColDef
    ) as ClioColDef[];
  },

  getCellErrors(
    rowId: string | undefined,
    field: string | undefined
  ): string[] | undefined {
    if (!rowId || !field) return undefined;
    return this.cellErrors[rowId]?.[field];
  },

  /**
   * Data grid-wide tooltip resolver. Errors win so validation messages always
   * surface; otherwise resolves tooltipField declaratively, since column
   * authors can't supply JavaScript getters through the Ruby/JSON column
   * config.
   */
  defaultTooltipValueGetter(params: ITooltipParams): string | undefined {
    const colDef = params.colDef as ColDef | undefined;
    const field = colDef?.field;
    const errors = this.getCellErrors(params.node?.id, field);
    if (errors?.length) return errors.join("\n");
    const tooltipField = colDef?.tooltipField;
    if (tooltipField) return params.data?.[tooltipField];
    return undefined;
  },

  /**
   * Build custom components for AG Grid, including the HTML renderer for custom
   * cell lambdas.
   */
  buildCustomComponents(): Record<string, any> {
    const components: Record<string, any> = {};

    // Add HTML renderer for columns that contain pre-rendered HTML
    components["htmlRenderer"] = HtmlRenderer;

    return components;
  },

  getStrategyOptions(): Partial<GridOptions> {
    if (this.config.dataStrategy === "clientSide") {
      return this.getClientSideRowModelOptions();
    }

    if (this.config.dataStrategy === "serverSide") {
      return this.getServerSideRowModelOptions();
    }

    return {};
  },

  /**
   * The client-side row model will load all of the data into the grid in one
   * go. The grid can then perform filtering, sorting, grouping, pivoting and
   * aggregation all in memory.
   *
   * @returns grid options for client-side row model.
   */
  getClientSideRowModelOptions(): Partial<GridOptions> {
    return {
      rowModelType: "clientSide",

      // Provides stable row identity using the `id` field. Required for
      // applyRowUpdates to correctly match incoming row data to existing nodes.
      getRowId: (params) => params.data?.id?.toString(),

      onGridReady: async (params: GridReadyEvent) => {
        this.gridApi = params.api;

        // If dataUrl is provided, fetch all data at once for client-side processing
        await this.fetchClientSideData(params.api);
      },
    };
  },

  /**
   * The server-side row model will delegate all data operations (sorting,
   * filtering, grouping, aggregation) to the server. This is ideal for
   * large datasets and when you want full server control over data operations.
   *
   * @returns grid options for server-side row model.
   */
  getServerSideRowModelOptions(): Partial<GridOptions> {
    return {
      rowModelType: "serverSide",
      cacheBlockSize: this.currentPageSize,

      /**
       * Provides a getRowId callback. Uses the 'id' field as the unique row
       * identifier. Providing a unique ID for each row allows the grid to work
       * optimally across a range of features including row selection, grouping,
       * and aggregation.
       *
       * @param params - The params object from AG Grid.
       * @returns the row ID.
       */
      getRowId: (params) => {
        if (
          params.data != null &&
          (params.data.id === undefined || params.data.id === null)
        ) {
          throw Error(
            "Server-side row model requires a unique `id` on each row."
          );
        }
        return params.data?.id?.toString();
      },

      /**
       * Provides a onGridReady callback. Prior to establishing the datasource,
       * the callback checks if an initial page is configured and sets the
       * redirection target start row if it is present. This is done to prevent
       * AG Grid from fetching records from pages leading up to the specified
       * initial page.
       *
       * @param params - The params object from AG Grid.
       */
      onGridReady: (params: GridReadyEvent) => {
        this.gridApi = params.api;

        if (this.config.page !== undefined && this.config.page > 0) {
          const pageSize = this.gridApi.paginationGetPageSize();
          this._redirectTargetStartRow = (this.config.page - 1) * pageSize;
          this.config.page = undefined;
        }

        const datasource = this.createServerSideDatasource();
        params.api.setGridOption("serverSideDatasource", datasource);
      },
    };
  },

  // ===== Row Selection Methods =====
  /**
   * Handle row selection changes from AG Grid. Stores the row data from
   * selected nodes so consumers can reactively access the selected row data.
   *
   * When pagination is enabled, intercepts the header "select all" behavior
   * to only expose rows on the current page. When pagination is disabled,
   * exposes every selected row the grid has loaded.
   *
   * @param event - The selection changed event from AG Grid.
   */
  handleSelectionChanged(_event: SelectionChangedEvent) {
    this.refreshSelectedRowsFromGrid();
  },

  /**
   * Rebuild the cached `selectedRows` snapshot from the grid's current
   * selection. AG Grid's `selectionChanged` fires only when the selection
   * *set* changes, not when a selected row's data is mutated by a transaction,
   * so call this after such updates to avoid a stale snapshot.
   */
  refreshSelectedRowsFromGrid() {
    if (!this.gridApi) return;

    if (this.gridApi.getGridOption("pagination") === true) {
      this.selectedRows = this.getSelectedRowsOnCurrentPage();
    } else if (this.config.dataStrategy === "serverSide") {
      this.selectedRows = this.getSelectedRowsFromNodes();
    } else {
      this.selectedRows = this.gridApi.getSelectedRows();
    }
  },

  /**
   * Resolve the selected rows by asking each loaded node whether the selection
   * covers it. A server-side select-all empties AG Grid's selected-node map, so
   * `getSelectedRows()` reports nothing afterwards even though every node
   * reports itself selected.
   *
   * @returns Array of selected row data the grid has loaded.
   */
  getSelectedRowsFromNodes(): any[] {
    if (!this.gridApi) return [];

    const rows: any[] = [];
    this.gridApi.forEachNode((node) => {
      if (node.isSelected() && node.data) {
        rows.push(node.data);
      }
    });

    return rows;
  },

  /**
   * Get selected rows that are on the current page only.
   *
   * In server-side row models, forEachNode only iterates over loaded nodes
   * in the cache, not all nodes across all pages. The method relies on
   * node.rowIndex which represents absolute row position across all pages.
   * Selected rows on unloaded pages won't be included, but this is acceptable
   * since selection is cleared on page navigation.
   */
  getSelectedRowsOnCurrentPage(): any[] {
    if (!this.gridApi) return [];

    const currentPage = this.gridApi.paginationGetCurrentPage();
    const pageSize = this.gridApi.paginationGetPageSize();
    const startRow = currentPage * pageSize;
    const endRow = startRow + pageSize;

    const selectedOnCurrentPage: any[] = [];
    this.gridApi.forEachNode((node) => {
      if (
        node.isSelected() &&
        node.data &&
        node.rowIndex !== null &&
        node.rowIndex >= startRow &&
        node.rowIndex < endRow
      ) {
        selectedOnCurrentPage.push(node.data);
      }
    });

    return selectedOnCurrentPage;
  },

  /**
   * Get the currently selected rows.
   *
   * @returns Array of selected row data.
   */
  getSelectedRows() {
    return this.selectedRows;
  },

  /**
   * Get the count of currently selected rows.
   *
   * @returns Number of selected rows.
   */
  getSelectedRowCount() {
    return this.selectedRows.length;
  },

  /**
   * Deselect all rows.
   */
  deselectAllRows() {
    if (this.gridApi) {
      this.gridApi.deselectAll();
    }
  },

  /**
   * Clear the selection ahead of anything that re-windows the visible page —
   * paging, a page-size change, a sort, or a filter. Only a paginated grid's
   * `selectedRows` snapshot is windowed to the current page, so with pagination
   * off this is a no-op: the selection spans every loaded row and clearing
   * would discard one the user built on purpose. `deselectAllRows` stays
   * unguarded, as it is also how a consumer clears the selection deliberately.
   */
  clearWindowedSelection() {
    if (this.gridApi?.getGridOption("pagination") === true) {
      this.deselectAllRows();
    }
  },

  /**
   * Select all rows.
   */
  selectAllRows() {
    if (this.gridApi) {
      this.gridApi.selectAll();
    }
  },

  /**
   * Select specific rows by their IDs. Works with both client-side and
   * server-side row models.
   *
   * For server-side: Uses setServerSideSelectionState to handle rows that may
   * not be loaded yet due to virtualization.
   *
   * * For client-side: Iterates through loaded nodes and selects matching rows.
   *
   * @param rowIds - The string IDs of the rows to select.
   */
  selectRows(...rowIds: string[]) {
    if (!this.gridApi || rowIds.length === 0) return;

    if (this.config.dataStrategy === "serverSide") {
      // For server-side, use setServerSideSelectionState which handles
      // virtualized rows that may not be loaded yet
      this.gridApi.setServerSideSelectionState({
        selectAll: false,
        toggledNodes: rowIds,
      });
    } else {
      // For client-side, iterate through nodes and select matching rows
      const rowIdSet = new Set(rowIds);
      this.gridApi.forEachNode((node) => {
        if (node.data && rowIdSet.has(String(node.data.id))) {
          node.setSelected(true);
        }
      });
    }
  },

  /**
   * Check if there are any selected rows.
   *
   * @returns Boolean indicating if any rows are selected.
   */
  hasSelectedRows(): boolean {
    return this.selectedRows.length > 0;
  },

  /**
   * Handle a cell value change event from AG Grid. Dispatches a bubbling
   * `data-grid:cell-value-changed` DOM event so consumers can react without
   * needing direct access to Alpine state.
   *
   * @param event - The cell value changed event from AG Grid.
   */
  handleCellValueChanged(event: CellValueChangedEvent) {
    const field = event.colDef.field;
    const oldValue = event.oldValue;
    const newValue = event.newValue;
    const rowData = { ...event.data };

    this.$dispatch("data-grid:cell-value-changed", {
      field,
      oldValue,
      newValue,
      rowData,
    });
  },

  /**
   * Stamp a built-in editor's `cellEditorElementAttributes` onto its input
   * element when editing starts — the path that lets built-in editors (e.g.
   * LargeTextCellEditor) carry forwarded attributes, since AG Grid owns their
   * construction. Custom-element editors get the same attributes at
   * construction (see processColumnDefs); only the built-in case is stashed on
   * `colDef.context` for this handler.
   */
  applyCellEditorElementAttributes(event: CellEditingStartedEvent) {
    const attributes = (
      event.colDef.context as
        { cellEditorElementAttributes?: Record<string, string> } | undefined
    )?.cellEditorElementAttributes;
    if (!attributes) return;

    // `getGui` isn't on the public ICellEditor type, but the built-in editors
    // are Component subclasses that expose it. This reaches past the interface
    // deliberately — it's the only way to the live editor's DOM — so it's
    // guarded (`?.`) and degrades to a no-op if a future AG version returns an
    // instance without it.
    const editor = event.api.getCellEditorInstances({
      columns: [event.column],
    })[0] as { getGui?: () => HTMLElement } | undefined;
    const gui = editor?.getGui?.();
    if (!gui) return;

    // Target the editor's input element so the attributes land where a consumer
    // expects them — e.g. an Alpine directive binds the textarea itself.
    const target =
      gui instanceof HTMLInputElement || gui instanceof HTMLTextAreaElement
        ? gui
        : (gui.querySelector("input, textarea") ?? gui);

    for (const [name, value] of Object.entries(attributes)) {
      target.setAttribute(name, value);
    }
  },

  restoreRowHeightAfterInlineLargeText(event: CellEditingStoppedEvent) {
    const isInlineLargeText = (
      event.colDef.context as { inlineLargeText?: boolean } | undefined
    )?.inlineLargeText;
    if (!isInlineLargeText) return;

    event.api.redrawRows({ rowNodes: [event.node] });
  },

  /**
   * Get the localStorage key for persisting grid state.
   *
   * @returns The persistence key if configured, null otherwise.
   */
  persistenceKey(): string | null {
    return this.config.persistenceId ?? null;
  },

  /**
   * Resolve the text wrap state: the user's saved choice, else the grid's
   * declared default. A stored preference is never overridden.
   *
   * @returns Whether rows should render with text wrapped.
   */
  resolveTextWrapState(): boolean {
    return (
      this.loadPersistedState()?.isTextWrapped ??
      this.config.defaultExpandRows ??
      false
    );
  },

  /**
   * Load and parse the persisted grid state from localStorage.
   *
   * @returns The parsed persisted state if found and valid, null otherwise.
   */
  loadPersistedState(): PersistedState | null {
    const key = this.persistenceKey();

    if (!key) return null;

    const localStorageValue = window.localStorage.getItem(key);

    if (!localStorageValue) return null;

    try {
      const persistedState = JSON.parse(localStorageValue) as PersistedState;

      if (!persistedState.gridState) return persistedState;

      return {
        ...persistedState,
        gridState: filterPersistedGridState(
          persistedState.gridState,
          this.config.excludedPersistedStateKeys
        ),
      };
    } catch (_parseError) {
      // Ignore corrupt values, allow grid to continue
      console.error(
        "[DATA GRID] Ignoring invalid persisted state during load",
        {
          key,
        }
      );
      return null;
    }
  },

  /**
   * Save the current grid state to localStorage.
   *
   * @param api - The grid API instance.
   */
  savePersistedStateDebounced(api: GridApi) {
    const key = this.persistenceKey();
    if (!key || !api) return;

    // Cancel any pending save so rapid event bursts coalesce into a single write
    if (this._persistTimer) clearTimeout(this._persistTimer);

    // Debounce the save to prevent excessive writes
    this._persistTimer = setTimeout(() => {
      // Capture text wrap state from the grid's current defaultColDef
      const isTextWrapped =
        api.getGridOption("defaultColDef")?.wrapText ?? false;

      const payload: PersistedState = {
        version: 2, // Incremented to 2 to include isTextWrapped property.
        gridState: filterPersistedGridState(
          api.getState(),
          this.config.excludedPersistedStateKeys
        ),
        isTextWrapped,
        meta: {
          savedAt: new Date().toISOString(),
        },
      };

      // Save the payload to localStorage.
      try {
        window.localStorage.setItem(key, JSON.stringify(payload));
      } catch (_storageError) {
        // Swallow storage errors so grid UX is not disrupted
        console.error(
          "[DATA GRID] Failed to persist state to localStorage; continuing",
          { key }
        );
      }
    }, 100);
  },

  /**
   * Reload the pagination variables and display information based on the
   * values provided by the grid API. These values are updated by the grid upon
   * interactions with the grid.
   */
  updatePaginationInfo() {
    if (this.gridApi) {
      this.currentPage = this.gridApi.paginationGetCurrentPage();
      this.totalPages = this.gridApi.paginationGetTotalPages();
      this.currentPageSize = this.gridApi.paginationGetPageSize();

      // getDisplayedRowCount() includes child rows, while paginationGetRowCount() does not
      // Why we'd want to use paginationGetRowCount() is because getDisplayedRowCount() counts custom detail rows as child rows,
      // so when a custom detail row is expanded, the total row count is not updated to include the child rows.
      if (this.config.hasCustomDetailTemplate) {
        this.totalRowCount = this.gridApi.paginationGetRowCount();
      } else {
        this.totalRowCount = this.gridApi.getDisplayedRowCount();
      }

      // Update the pagination range after values change
      this.updatePaginationRange();
    }
  },

  /**
   * Navigate to the next page in the pagination sequence.
   * Clears the selection, which is windowed to the visible page.
   */
  goToNextPage() {
    if (this.gridApi) {
      this.clearWindowedSelection();
      this.gridApi.paginationGoToNextPage();
    }
  },

  /**
   * Navigate to the previous page in the pagination sequence.
   * Clears the selection, which is windowed to the visible page.
   */
  goToPreviousPage() {
    if (this.gridApi) {
      this.clearWindowedSelection();
      this.gridApi.paginationGoToPreviousPage();
    }
  },

  /**
   * Navigate to the first page in the pagination sequence.
   * Clears the selection, which is windowed to the visible page.
   */
  goToFirstPage() {
    if (this.gridApi) {
      this.clearWindowedSelection();
      this.gridApi.paginationGoToFirstPage();
    }
  },

  /**
   * Navigate to the last page in the pagination sequence.
   * Clears the selection, which is windowed to the visible page.
   */
  goToLastPage() {
    if (this.gridApi) {
      this.clearWindowedSelection();
      this.gridApi.paginationGoToLastPage();
    }
  },

  /**
   * Check if the first page is available in the pagination sequence. Used to
   * determine if the first page button should be disabled.
   */
  isFirstPageAvailable() {
    return this.currentPage > 0;
  },

  /**
   * Check if the next page is available in the pagination sequence. Used to
   * determine if the next page button should be disabled.
   */
  isNextPageAvailable() {
    return this.currentPage < this.totalPages - 1;
  },

  /**
   * Check if the previous page is available in the pagination sequence. Used to
   * determine if the previous page button should be disabled.
   */
  isPreviousPageAvailable() {
    return this.currentPage > 0;
  },

  /**
   * Check if the last page is available in the pagination sequence. Used to
   * determine if the last page button should be disabled.
   */
  isLastPageAvailable() {
    return this.currentPage < this.totalPages - 1;
  },

  /**
   * Called when the user changes the number of records to show per page.
   * Updates the grid API's paginationPageSize option, rowBuffer option, and cacheBlockSize option
   * if the data strategy is server-side. Clears the selection, because a new
   * page size re-windows the visible page.
   */
  changePageSize(newPageSize: number) {
    if (this.gridApi && newPageSize > 0) {
      this.clearWindowedSelection();
      this.gridApi.setGridOption("paginationPageSize", newPageSize);
      this.gridApi.setGridOption("rowBuffer", newPageSize);

      // Update cache block size for server-side row models
      if (this.config.dataStrategy === "serverSide") {
        this.gridApi.setGridOption("cacheBlockSize", newPageSize);
      }

      // Update pagination range after page size change
      this.updatePaginationRange();
    }
  },

  /**
   * Update the pagination range information based on the current page, page
   * size, and total row count.
   */
  updatePaginationRange() {
    const recordRangeStart =
      this.totalRowCount > 0 ? this.currentPage * this.currentPageSize + 1 : 0;
    const recordRangeEnd = Math.min(
      (this.currentPage + 1) * this.currentPageSize,
      this.totalRowCount
    );

    // Update the span elements with current pagination values using Alpine refs
    if (this.$refs.paginationStart) {
      this.$refs.paginationStart.textContent = recordRangeStart.toString();
    }
    if (this.$refs.paginationEnd) {
      this.$refs.paginationEnd.textContent = recordRangeEnd.toString();
    }
    if (this.$refs.paginationTotal) {
      const total = this.totalRowCount.toString();
      this.$refs.paginationTotal.textContent = this.isRowCountCapped
        ? `${total}+`
        : total;
    }
  },

  /**
   * Submit the query params to filter the results. This method will perform the
   * filtering based on the data strategy before syncing the query parameters to
   * the browser URL params. Clears the selection first, because a filter
   * re-windows the visible page the same way paging or a page-size change does.
   *
   * @param queryParams - The query params to use for filtering. Can be an
   * object or an HTMLFormElement.
   */
  submitQueryParams(queryParams: QueryParams | HTMLFormElement) {
    const isFormElement = queryParams instanceof HTMLFormElement;
    const params: QueryParams = isFormElement
      ? formDataToQueryParams(queryParams)
      : queryParams;

    // When a form element is submitted, detect keys that were present in a
    // previous submission but are now absent (e.g., a multi-select whose
    // hidden inputs were removed from the DOM when all items were cleared).
    // Inject empty values so downstream logic deletes them from the URL and
    // server request instead of leaving stale params behind.
    if (isFormElement) {
      const currentKeys = new Set(Object.keys(params));
      if (this._previousFormKeys) {
        for (const key of this._previousFormKeys) {
          if (!currentKeys.has(key)) {
            params[key] = key.endsWith("[]") ? [] : "";
          }
        }
      }
      this._previousFormKeys = currentKeys;
    }

    // Both strategies re-window the visible page from here. Clear before
    // dispatching so a consumer listening for data-grid:filter never reads a
    // stale count.
    this.clearWindowedSelection();

    this.$dispatch("data-grid:filter", { queryParams: params });

    if (this.config.dataStrategy === "clientSide") {
      this.handleClientSideFilter(params);
    }

    if (this.config.dataStrategy === "serverSide") {
      this.handleServerSideFilter(params);
    }

    this.syncQueryParamsToUrlParams(params);
  },

  /**
   * Set the quick filter text in the grid. This is used to filter the grid
   * based on the query, only if the grid is using the client-side row model.
   *
   * @param queryParams - The filter params to use for filtering
   */
  handleClientSideFilter(queryParams: QueryParams) {
    if (
      this.gridApi &&
      queryParams.query !== undefined &&
      queryParams.query !== null
    ) {
      this.gridApi.setGridOption("quickFilterText", queryParams.query);
    }
  },

  /**
   * Handle page redirection for server-side row model requests. This is used to
   * optimize page navigation by skipping unnecessary data fetches when
   * refreshing the data grid from a page other than the first.
   *
   * This method intercepts getRows() requests and determines whether to A) skip
   * the request (return empty data) and navigate to a different page, or B)
   * allow the request to proceed normally.
   *
   * @param rowParams - The AG Grid row params from getRows().
   * @returns true if the request was handled (skipped/redirected), or false to
   * proceed normally.
   */
  handlePageRedirection(rowParams: IServerSideGetRowsParams): boolean {
    // No redirection needed if the redirect target start row is not set
    if (this._redirectTargetStartRow === null) {
      return false;
    }

    const { startRow } = rowParams.request;

    // We've reached or passed the target page - clear flag and proceed normally
    if (startRow >= this._redirectTargetStartRow) {
      this._redirectTargetStartRow = null;
      return false;
    }

    // Skip this request and navigate to the target page
    const pageSize = this.gridApi.paginationGetPageSize();
    const targetPage = Math.floor(this._redirectTargetStartRow / pageSize);

    /**
     * Provide a placeholder row count to keep AG Grid's pagination working
     * while we skip unwanted page fetches. The real row count comes from the
     * server when we actually fetch the target page's data.
     */
    rowParams.success({
      rowData: [],
      rowCount: (targetPage + PAGINATION_BUFFER_PAGES) * pageSize,
    });

    // Navigate to the target page if we're not already there
    if (this.gridApi.paginationGetCurrentPage() !== targetPage) {
      setTimeout(() => this.gridApi.paginationGoToPage(targetPage), 0);
    }

    // Request handled - skip normal data fetch
    return true;
  },

  /**
   * Create a server-side datasource for the grid. This method encapsulates the
   * logic for creating a datasource that can handle server-side operations like
   * sorting, filtering, grouping, and pagination.
   *
   * @param queryParams - Optional query params to use for filtering.
   * @returns An IServerSideDatasource object for AG Grid's server-side row
   * model.
   */
  createServerSideDatasource(queryParams?: QueryParams): IServerSideDatasource {
    return {
      getRows: async (rowParams: IServerSideGetRowsParams) => {
        try {
          /**
           * Handle page redirection for refresh operations. Return immediately
           * if the request was handled by redirection logic.
           */
          if (this.handlePageRedirection(rowParams)) {
            return;
          }

          /**
           * If the request is for grouping, don't set the loading state to true
           * because instead of the overlay loading indicator, the group row
           * loading indicator will be shown.
           */
          const isGrouping =
            rowParams.request.groupKeys &&
            rowParams.request.groupKeys.length > 0;
          if (!isGrouping) {
            this.gridApi.setGridOption("loading", true);
          }

          const [path, paramString] = this.config.dataUrl.split("?");

          const searchParams = new URLSearchParams(paramString);

          const requestParams = new URLSearchParams({
            ...Object.fromEntries(searchParams.entries()),
            startRow: rowParams.request.startRow?.toString() || "0",
            endRow:
              rowParams.request.endRow?.toString() ||
              this.currentPageSize.toString(),
            page: this.gridApi.paginationGetCurrentPage().toString(),
            pageSize: this.gridApi.paginationGetPageSize().toString(),
          });

          // Add search query if query params are provided
          // Hash query - append each key-value pair as separate parameters
          if (queryParams) {
            Object.entries(queryParams).forEach(([key, value]) => {
              if (Array.isArray(value)) {
                requestParams.delete(key);
                value.forEach((v) => {
                  const trimmed = v.trim();
                  if (trimmed) requestParams.append(key, trimmed);
                });
              } else {
                const trimmedValue = value.trim();
                trimmedValue
                  ? requestParams.set(key, trimmedValue)
                  : requestParams.delete(key);
              }
            });
          }

          // Add sorting parameters if present
          if (
            rowParams.request.sortModel &&
            rowParams.request.sortModel.length > 0
          ) {
            requestParams.append(
              "sortModel",
              JSON.stringify(rowParams.request.sortModel)
            );
          }

          // Add grouping parameters for server-side row grouping
          if (
            rowParams.request.rowGroupCols &&
            rowParams.request.rowGroupCols.length > 0
          ) {
            requestParams.append(
              "rowGroupCols",
              JSON.stringify(rowParams.request.rowGroupCols)
            );
          }

          // Add group keys for expanded groups
          if (
            rowParams.request.groupKeys &&
            rowParams.request.groupKeys.length > 0
          ) {
            requestParams.append(
              "groupKeys",
              JSON.stringify(rowParams.request.groupKeys)
            );
          }

          const url = `${path}?${requestParams.toString()}`;

          const response = await this.fetchData(url);

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          const data = await readJsonBody(response);

          const { aggregations } = data;

          if (aggregations) {
            const pinnedBottomRowData = [{ ...aggregations }];
            this.gridApi.setGridOption(
              "pinnedBottomRowData",
              pinnedBottomRowData
            );
          }

          this.gridApi.setGridOption("loading", false);

          this.isRowCountCapped = data.rowCountCapped === true;

          rowParams.success({
            rowData: data.rows,
            rowCount: data.rowCount,
          });
        } catch (error) {
          console.error("[DATA GRID] Error loading server-side data: ", error);

          this.gridApi.setGridOption("loading", false);

          rowParams.fail();
        }
      },
    };
  },

  /**
   * Refreshes the server-side datasource with the query params.
   * Only used for server-side row model.
   *
   * @param queryParams - The query params to use for filtering.
   */
  handleServerSideFilter(queryParams: QueryParams) {
    if (!this.gridApi) return;

    this.gridApi.setGridOption("loading", true);

    // Clear any pending page redirection when filters change
    this._redirectTargetStartRow = null;

    // In client-side row model, setting quickFilterText will allow the grid to
    // to know that the grid is filtered, and the no matches overlay will be
    // shown. In server-side row model, we need to set a flag to indicate that
    // the grid is filtered.
    this.hasExternalFilter = Object.values(queryParams).some((value) => {
      if (Array.isArray(value)) {
        return value.some((v) => v && v.trim().length > 0);
      }
      return value && value.trim().length > 0;
    });

    const datasource = this.createServerSideDatasource(queryParams);
    this.gridApi.setGridOption("serverSideDatasource", datasource);
  },

  /**
   * Handle hiding and showing the no rows overlay depending on the number of
   * rows in the grid. Because the loading state is also an overlay, we need to
   * check if the loading overlay is active before toggling the visibility of
   * the no rows overlay.
   *
   * @param event - The event object from the grid API.
   */
  handleNoRowsOverlay(event: ModelUpdatedEvent) {
    const gridApi = event.api;
    const displayedRowCount = gridApi.getDisplayedRowCount();
    const isLoading = gridApi.getGridOption("loading");

    // Loading overlay takes precedence over the no rows overlay
    if (isLoading) return;

    if (displayedRowCount === 0) {
      const isFiltered = this.hasActiveFilters(gridApi);

      if (isFiltered) {
        const template = this.config.noMatchesTemplate;
        gridApi.setGridOption("overlayNoRowsTemplate", template);
      } else {
        const template = this.config.noRecordsTemplate;
        gridApi.setGridOption("overlayNoRowsTemplate", template);
      }
      gridApi.showNoRowsOverlay();
    } else {
      gridApi.hideOverlay();
    }
  },

  /**
   * Check if any filters are currently active in the data grid.
   *
   * @param gridApi - The grid API instance.
   * @returns true if any filters are active, false otherwise.
   */
  hasActiveFilters(gridApi: GridApi): boolean {
    return gridApi.isAnyFilterPresent() || this.hasExternalFilter;
  },

  /**
   * Sync filter-related query parameters to the browser URL params. If a user
   * performs a search using the search component, filters data using the
   * segmented filter component, or performs any other custom filtering
   * operation, the query parameters will be propagated to the browser URL
   * params.
   *
   * @param params - The query params to sync to the URL params.
   */
  syncQueryParamsToUrlParams(params: QueryParams) {
    if (!this.config.enableUrlStatePersistence) return;

    const { baseUrl, hashPath, queryParams, isHashRouting } = parseUrl();

    // Update with new params
    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        queryParams.delete(key);
        value.forEach((v) => {
          const trimmed = v.trim();
          if (trimmed) queryParams.append(key, trimmed);
        });
      } else if (value && value.toString().trim()) {
        queryParams.set(key, value.toString());
      } else {
        queryParams.delete(key);
      }
    });

    const newUrl = buildUrl(baseUrl, hashPath, queryParams, isHashRouting);
    // Preserve existing history state (e.g., Turbo's restoration data) while
    // only updating the URL. Using {} would wipe out Turbo's state tracking.
    window.history.replaceState(window.history.state, "", newUrl);
  },

  /**
   * Sync all grid state to the browser URL params. This includes pagination
   * and sort state. Debounced to prevent excessive history.replaceState() calls
   * during rapid grid events.
   */
  syncGridStateToUrlParams() {
    if (!this.config.enableUrlStatePersistence || !this.gridApi) return;

    // Cancel any pending sync so rapid event bursts coalesce into a single write
    if (this._urlSyncTimer) clearTimeout(this._urlSyncTimer);

    // Debounce the URL update to prevent excessive writes
    this._urlSyncTimer = setTimeout(() => {
      const { baseUrl, hashPath, queryParams, isHashRouting } = parseUrl();

      // Sync pagination state
      const currentPage = this.gridApi.paginationGetCurrentPage();
      const currentPageSize = this.gridApi.paginationGetPageSize();

      if (currentPage > 0) {
        queryParams.set("page", (currentPage + 1).toString());
      } else {
        queryParams.delete("page");
      }

      if (currentPageSize !== this.config.gridOptions?.paginationPageSize) {
        queryParams.set("page_size", currentPageSize.toString());
      } else {
        queryParams.delete("page_size");
      }

      // Sync sort state
      const columnState = this.gridApi.getColumnState();
      const sortedColumns = columnState.filter((col: any) => col.sort);

      if (sortedColumns.length > 0) {
        const sortedColumn = sortedColumns[0];
        queryParams.set("sort_column", sortedColumn.colId);
        queryParams.set("sort_direction", sortedColumn.sort);
      } else {
        queryParams.delete("sort_column");
        queryParams.delete("sort_direction");
      }

      const newUrl = buildUrl(baseUrl, hashPath, queryParams, isHashRouting);
      // Preserve existing history state (e.g., Turbo's restoration data) while
      // only updating the URL. Using {} would wipe out Turbo's state tracking.
      window.history.replaceState(window.history.state, "", newUrl);
    }, 100);
  },

  /**
   * Text wrap state for the ExpandRowComponent switch. Shares
   * resolveTextWrapState with initDataGrid so the two cannot disagree.
   *
   * @returns true if rows should render with text wrapped, false otherwise.
   */
  getPersistedTextWrap(): boolean {
    return this.resolveTextWrapState();
  },

  /**
   * Toggles between text wrapping and truncation, and persists the state.
   *
   * @param checked - Whether to enable text wrapping.
   */
  toggleTextWrap(checked: boolean) {
    this.setTextWrapState(checked);
    if (this.gridApi) {
      this.savePersistedStateDebounced(this.gridApi);
    }
  },

  /**
   * Applies text wrap setting to the grid without persisting.
   * Used internally for both initialization and toggle operations.
   *
   * @param checked - Whether to enable text wrapping.
   */
  setTextWrapState(checked: boolean) {
    if (!this.gridApi) return;

    const currentDefaultColDef =
      this.gridApi.getGridOption("defaultColDef") || {};

    // Strip any previously set whiteSpace so toggling off cleanly removes it.
    const { whiteSpace: _prev, ...restCellStyle } =
      (currentDefaultColDef.cellStyle as Record<string, unknown>) || {};

    // Capture current column state before updating defaultColDef.
    // AG Grid re-evaluates column definitions when setGridOption("defaultColDef")
    // is called, which resets both visibility and column order back to the original
    // columnDefs. We restore the full state (including order) after the update.
    const columnState = this.gridApi.getColumnState();

    this.gridApi.setGridOption("defaultColDef", {
      ...currentDefaultColDef,
      wrapText: checked,
      // pre-wrap: \n in cell data renders as a visible line break (rows expand).
      // nowrap:   \n is ignored, text stays on one line (rows collapse).
      // AG Grid's wrapText only handles width overflow — it does not interpret
      // \n characters. white-space must be set explicitly for that to work.
      cellStyle: {
        ...restCellStyle,
        whiteSpace: checked ? "pre-wrap" : "nowrap",
      },
    });

    // Restore column visibility and order so user-toggled visibility and
    // user-reordered columns aren't reset by the defaultColDef re-evaluation.
    this.gridApi.applyColumnState({ state: columnState, applyOrder: true });

    // Force row height recalculation for existing rows. AG Grid updates the column
    // definition but doesn't automatically recalculate heights of existing rows when
    // wrapText changes, which can result in inconsistent row heights until rows are
    // re-rendered (e.g., by scrolling out of view and back).
    this.gridApi.resetRowHeights();
  },

  /**
   * Performs the HTTP request for grid data. Replace it to control the verb,
   * body or headers; the grid has already built `url` and still interprets the
   * response, so an override only owns the transport.
   */
  async fetchData(url: string) {
    return get(url);
  },

  async fetchClientSideData(gridApi: GridApi) {
    if (this.config.dataUrl) {
      try {
        gridApi.setGridOption("loading", true);

        const response = await this.fetchData(this.config.dataUrl);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await readJsonBody(response);

        // Data should be an array
        const rowData = Array.isArray(data) ? data : [];
        gridApi.setGridOption("rowData", rowData);
      } catch (error) {
        console.error("[DATA GRID] Error loading client-side data: ", error);
        // Set empty array as fallback
        gridApi.setGridOption("rowData", []);
      } finally {
        gridApi.setGridOption("loading", false);
      }
    }
  },

  /**
   * Apply preselected rows from configuration. For client-side, this is called
   * after data is loaded. For server-side, this is called when the datasource
   * is set.
   */
  applyPreselectedRows() {
    const rowIds = this.config.preselectedRowIds;
    if (!rowIds || rowIds.length === 0) return;

    this.selectRows(...rowIds);
  },

  /**
   * Refreshes the data grid based on the data strategy.
   *
   * If the strategy is client-side, all the data will be re-fetched from the
   * server and set as the row data.
   *
   * If the strategy is server-side, the current page will be captured and the
   * redirect target start row will be set if the current page is greater than
   * 0. Calling refreshServerSide() with { purge: true } causes all cached rows
   * to be cleared. This is important for grouped rows where child nodes need to
   * refresh.
   */
  async refreshDataGrid() {
    if (!this.gridApi) return;

    this.$dispatch("data-grid:refresh");

    if (this.config.dataStrategy === "clientSide") {
      await this.fetchClientSideData(this.gridApi);
    }

    if (this.config.dataStrategy === "serverSide") {
      const currentPage = this.gridApi.paginationGetCurrentPage();
      const pageSize = this.gridApi.paginationGetPageSize();

      if (currentPage > 0) {
        this._redirectTargetStartRow = currentPage * pageSize;
      }

      this.gridApi.refreshServerSide({ purge: true });
    }
  },

  /**
   * Apply a partial row update to the grid in-place and flash only the changed
   * cell to give users immediate visual feedback.
   *
   * Pass an object containing `id` plus only the fields that should change.
   * The current row data is fetched from the grid and merged with the partial
   * update so that unchanged fields are preserved. Only the columns present in
   * the partial update are flashed.
   *
   * Throws if no row with the given `id` exists in the grid.
   *
   * Supports both client-side (applyTransaction) and server-side
   * (applyServerSideTransaction) row models. To update multiple rows, call
   * this method once per row.
   *
   * @param partial - Partial row object containing `id` and the fields to update.
   */
  applyRowUpdates(partial: Record<string, unknown>) {
    if (!this.gridApi) return;

    const changedColumns = Object.keys(partial).filter((key) => key !== "id");
    const rowNode = this.gridApi.getRowNode(String(partial.id));

    if (!rowNode) {
      throw Error(`applyRowUpdates: no row found with id "${partial.id}"`);
    }

    const rowData = { ...rowNode.data, ...partial };
    let updatedNodes: any[] = [];

    if (this.config.dataStrategy === "serverSide") {
      const result = this.gridApi.applyServerSideTransaction({
        update: [rowData],
      });
      updatedNodes = result?.update ?? [];
    } else {
      const result = this.gridApi.applyTransaction({ update: [rowData] });
      updatedNodes = result?.update ?? [];
    }

    if (updatedNodes.length) {
      // Re-evaluate cellClass for the whole updated row. applyTransaction only
      // re-renders cells whose *value* changed, so a row whose editability
      // flips via its `${field}_editable` flags — without the cell's own value
      // changing — would keep stale cellClass output (e.g. the editable-cell
      // affordance) until the cell is redrawn for some other reason. Forcing a
      // refresh across all columns keeps that styling in sync; suppressFlash
      // leaves the explicit flashCells call below as the only visible flash.
      this.gridApi.refreshCells({
        rowNodes: updatedNodes,
        force: true,
        suppressFlash: true,
      });

      this.gridApi.flashCells({
        rowNodes: updatedNodes,
        columns: changedColumns,
      });
    }

    this.refreshSelectedRowsFromGrid();
  },

  /**
   * Set error messages for specific cells and refresh the grid so that the
   * error styling and tooltips become visible. Each call replaces any existing
   * errors for the specified rows — it does not merge with previous state.
   * Rows not mentioned in the argument keep their existing errors.
   *
   * @param errors - Object mapping row IDs to field-to-messages mappings.
   */
  applyRowErrors(errors: Record<string, Record<string, string[]>>) {
    if (!this.gridApi) return;

    for (const [rowId, fieldErrors] of Object.entries(errors)) {
      this.cellErrors[rowId] = fieldErrors;
    }

    const affectedNodes = Object.keys(errors)
      .map((rowId) => this.gridApi!.getRowNode(rowId))
      .filter(Boolean);

    this.gridApi.refreshCells({
      rowNodes: affectedNodes,
      force: true,
      suppressFlash: true,
    });
  },

  /**
   * Remove error messages from specific rows and refresh the grid so that
   * the error styling and tooltips are cleared.
   *
   * @param rowIds - Array of row IDs to clear errors for.
   */
  clearRowErrors(rowIds: string[]) {
    if (!this.gridApi) return;

    for (const rowId of rowIds) {
      delete this.cellErrors[rowId];
    }

    const affectedNodes = rowIds
      .map((rowId) => this.gridApi!.getRowNode(rowId))
      .filter(Boolean);

    if (affectedNodes.length) {
      this.gridApi.refreshCells({
        rowNodes: affectedNodes,
        force: true,
        suppressFlash: true,
      });
    }
  },

  /**
   * Custom keyboard event handler to enable tabbing through interactive
   * elements within grid cells. This implements the AG Grid recommendation for
   * custom cell components with focusable elements.
   *
   * @param params - The keyboard event parameters from AG Grid.
   * @returns true to suppress the default grid behavior, false to allow it.
   */
  suppressKeyboardEvent(params: SuppressKeyboardEventParams): boolean {
    const { event, colDef } = params;

    /**
     * Suppress Escape and Enter key events for cells with custom renderers to
     * ensure that AG Grid doesn't remove the cell's focused state. Components
     * within the cell (such as popovers and dropdowns) handle these keys
     * themselves. If the cell is being edited, enable default key handling to
     * allow users to exit the cell editor normally.
     */
    if ((event.key === "Escape" || event.key === "Enter") && !params.editing) {
      if (
        colDef.cellRenderer &&
        !(
          typeof colDef.cellRenderer === "string" &&
          colDef.cellRenderer !== "htmlRenderer"
        )
      ) {
        const cell = document.activeElement?.closest(".ag-cell") as HTMLElement;
        if (cell && this.getFocusableElements(cell).length > 0) return true;
      }
    }

    // Only handle Tab key events
    if (event.key !== "Tab") return false;

    // Only handle cells that have custom renderers
    if (!colDef.cellRenderer) return false;

    // Only handle our custom html renderer cells
    if (
      typeof colDef.cellRenderer === "string" &&
      colDef.cellRenderer !== "htmlRenderer"
    ) {
      return false;
    }

    // Find the cell element from the current focused element
    const cell = document.activeElement?.closest(".ag-cell") as HTMLElement;
    if (!cell) return false;

    // Find all focusable elements within the cell
    const focusableElements = this.getFocusableElements(cell);
    if (focusableElements.length === 0) return false;

    const currentIndex = focusableElements.indexOf(
      document.activeElement as HTMLElement
    );
    const isShiftTab = event.shiftKey;

    let nextIndex: number;

    // Moving backwards
    if (isShiftTab) {
      if (currentIndex <= 0) {
        // At or before first element - let grid handle navigation to previous cell
        return currentIndex === 0
          ? false
          : this.focusElement(
              event,
              focusableElements,
              focusableElements.length - 1
            );
      }
      nextIndex = currentIndex - 1;
    } else {
      if (currentIndex >= focusableElements.length - 1) {
        // At or beyond last element - let grid handle navigation to next cell
        return currentIndex === focusableElements.length - 1
          ? false
          : this.focusElement(event, focusableElements, 0);
      }
      nextIndex = currentIndex + 1;
    }

    // Focus the next/previous element within the cell
    return this.focusElement(event, focusableElements, nextIndex);
  },

  /**
   * Helper method to focus an element and prevent default event behavior.
   *
   * @param event - The keyboard event to prevent default behavior on.
   * @param elements - Array of focusable elements.
   * @param index - Index of the element to focus.
   * @returns true indicating the event was handled.
   */
  focusElement(
    event: KeyboardEvent,
    elements: HTMLElement[],
    index: number
  ): boolean {
    event.preventDefault();
    elements[index].focus();
    return true;
  },

  /**
   * Get all focusable elements within a given container element. This includes
   * buttons, inputs, links, and other interactive elements.
   *
   * @param container - The container element to search within.
   * @returns an array of focusable HTMLElements.
   */
  getFocusableElements(container: HTMLElement): HTMLElement[] {
    const focusableSelectors = [
      "button:not([disabled])",
      "input:not([disabled])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      "a[href]",
    ].join(", ");

    const elements = container.querySelectorAll(focusableSelectors);
    return Array.from(elements) as HTMLElement[];
  },

  /**
   * Check if a column is visible. Uses the persistence key to determine the
   * column visibility if it is available, otherwise uses the grid API.
   *
   * @param columnId - The ID of the column to check.
   * @returns true if the column is visible, false otherwise.
   */
  getColumnVisibility(columnId: string): boolean {
    const persistedState = this.loadPersistedState();

    if (persistedState) {
      const hiddenColIds =
        persistedState.gridState?.columnVisibility?.hiddenColIds || [];
      return !hiddenColIds.includes(columnId);
    } else if (this.gridApi) {
      const column = this.gridApi.getColumn(columnId);
      if (!column) {
        console.warn(
          `[DATA GRID] Column '${columnId}' not found in grid. Defaulting to visible.`
        );
        return true;
      }
      return column.isVisible();
    }

    console.warn(
      "[DATA GRID] Cannot determine column visibility: persistence ID not provided, and grid API is not available. Defaulting to visible."
    );

    return true;
  },

  /**
   * Set the visibility of a column using AG Grid's column API.
   *
   * @param columnId - The ID of the column to toggle.
   * @param visible - Whether the column should be visible.
   */
  setColumnVisibility(columnId: string, visible: boolean) {
    if (!this.gridApi) return;

    this.gridApi.setColumnsVisible([columnId], visible);
  },

  /**
   * Cleanup method called when the Alpine component is destroyed. Clears any
   * pending timers to prevent memory leaks.
   */
  destroy() {
    if (this._persistTimer) {
      clearTimeout(this._persistTimer);
      this._persistTimer = null;
    }
    if (this._urlSyncTimer) {
      clearTimeout(this._urlSyncTimer);
      this._urlSyncTimer = null;
    }
  },
});
